import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);

      if (response.data) {
        toast({
          title: 'Message Sent Successfully!',
          description: 'We will get back to you within 24 hours.',
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to send message',
        description: error.response?.data?.detail || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      id: 'office-address',
      icon: MapPin,
      title: 'Office Address',
      content: '32, Ultimate Business Center, M.G. Marg, Fort, Mumbai - 400001',
      link: 'https://maps.google.com/?q=32+Ultimate+Business+Center+MG+Marg+Fort+Mumbai+400001',
    },
    {
      id: 'phone-number',
      icon: Phone,
      title: 'Phone Number',
      content: '+91 9082738204',
      link: 'tel:9082738204',
    },
    {
      id: 'email-address',
      icon: Mail,
      title: 'Email Address',
      content: 'kanish@cakja.co.in',
      link: 'mailto:kanish@cakja.co.in',
    },
    {
      id: 'business-hours',
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon - Fri: 10:00 AM - 7:30 PM\nSat: 10:00 AM - 5:00 PM',
      link: null,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-700 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/8872614/pexels-photo-8872614.jpeg"
            alt="Contact"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-blue-100">
              Get in touch with our expert team. We're here to help your business succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info) => (
              <Card key={info.id} className="border-2 border-blue-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <info.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{info.title}</h3>
                  {info.link ? (
                    <a 
                      href={info.link}
                      target={info.icon === MapPin ? '_blank' : undefined}
                      rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                      className="text-gray-700 hover:text-blue-700 transition-colors whitespace-pre-line"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-gray-700 whitespace-pre-line">{info.content}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Form and Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {/* Contact Form */}
            <div>
              <Card className="border-2 border-blue-200 shadow-2xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us more about your requirements..."
                        rows={5}
                        className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2" size={20} />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Map and Additional Info */}
            <div className="space-y-8">
              {/* Map */}
              <Card className="border-2 border-blue-200 shadow-2xl overflow-hidden">
                <CardContent className="p-0">
                  <div className="h-96 w-full">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2877.9473415454163!2d72.82822464658844!3d18.930664283020498!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7d1dbe92a8b1d%3A0x128e96333e32fbad!2sUltimate%20Business%20Centre!5e0!3m2!1sen!2sin!4v1771775252367!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ultimate Business Centre, M.G. Marg, Fort, Mumbai"
                    ></iframe>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Contact Card */}
              <Card className="border-2 border-blue-200 shadow-2xl bg-gradient-to-br from-blue-700 to-blue-900 text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-4">Need Immediate Assistance?</h3>
                  <p className="text-blue-100 mb-6">
                    Our team is available during business hours to address your urgent queries.
                  </p>
                  <div className="space-y-4">
                    <a 
                      href="tel:9082738204"
                      className="flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-300"
                    >
                      <Phone className="flex-shrink-0" size={24} />
                      <div>
                        <div className="text-sm text-blue-200">Call Us Now</div>
                        <div className="font-bold">+91 9082738204</div>
                      </div>
                    </a>
                    <a 
                      href="mailto:kanish@cakja.co.in"
                      className="flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-300"
                    >
                      <Mail className="flex-shrink-0" size={24} />
                      <div>
                        <div className="text-sm text-blue-200">Email Us</div>
                        <div className="font-bold break-all">kanish@cakja.co.in</div>
                      </div>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Quick answers to common questions about our services
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  id: 'faq-services',
                  question: 'What services does your CA firm provide?',
                  answer: 'We offer comprehensive services including company registration, tax advisory, GST services, auditing, accounting, compliance, and financial advisory.',
                },
                {
                  id: 'faq-booking',
                  question: 'How can I book a consultation?',
                  answer: 'You can book a consultation by calling us at +91 9082738204, emailing us at kanish@cakja.co.in, or filling out the contact form above.',
                },
                {
                  id: 'faq-hours',
                  question: 'What are your business hours?',
                  answer: 'We are open Monday to Friday from 10:00 AM to 7:30 PM, and Saturday from 10:00 AM to 5:00 PM. We are closed on Sundays.',
                },
                {
                  id: 'faq-startups',
                  question: 'Do you provide services for startups?',
                  answer: 'Yes, we specialize in helping startups with company registration, compliance setup, tax planning, and ongoing financial management.',
                },
                {
                  id: 'faq-registration-time',
                  question: 'How long does the company registration process take?',
                  answer: 'Company registration typically takes 7-10 business days, depending on the type of entity and documentation provided.',
                },
              ].map((faq) => (
                <Card key={faq.id} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="text-white" size={18} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
